import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { GridColDef } from "@mui/x-data-grid";
import { useHistory, useLocation } from "react-router-dom";

import PageHeader from "../../app/layout/PageHeader";
import FrontlineDataGrid from "../../components/common/FrontlineDataGrid";
import {
  ContactIssueReportDto,
  getContactIssueReport,
} from "../../api/apiClient";
import "../../css/buttons.css";
import "../../css/cards.css";
import "../../css/forms.css";
import "../../css/tables.css";
import "./ContactsReportPage.css";

type IssueFilter =
  | "All"
  | "Missing Email"
  | "Missing Mobile"
  | "Unverified Email"
  | "Unverified Mobile"
  | "Not Linked To Service";

type ContactIssueRow = ContactIssueReportDto & {
  id: number;
};

const issueFilters: IssueFilter[] = [
  "All",
  "Missing Email",
  "Missing Mobile",
  "Unverified Email",
  "Unverified Mobile",
  "Not Linked To Service",
];

const getEmailStatus = (row: ContactIssueReportDto) => {
  if (!row.hasEmail) return "Missing";
  return row.emailVerified ? "Verified" : "Not Verified";
};

const getMobileStatus = (row: ContactIssueReportDto) => {
  if (!row.hasMobile) return "Missing";
  return row.mobileVerified ? "Verified" : "Not Verified";
};

const getStatusClass = (status: string) =>
  `contacts-report-status contacts-report-status--${status
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

export default function ContactsReportPage() {
  const history = useHistory();
  const location = useLocation();
  const serviceId = Number(new URLSearchParams(location.search).get("serviceId")) || null;
  const [rows, setRows] = useState<ContactIssueRow[]>([]);
  const [search, setSearch] = useState("");
  const [issueFilter, setIssueFilter] = useState<IssueFilter>("All");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadReport = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await getContactIssueReport(serviceId);

        if (isMounted) {
          setRows(result.map((item) => ({ ...item, id: item.partyId })));
        }
      } catch (loadError) {
        console.error("Failed to load contact issue report", loadError);
        if (isMounted) {
          setError("Failed to load contact issue report.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadReport();

    return () => {
      isMounted = false;
    };
  }, [serviceId]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesSearch =
        !term ||
        `${row.displayName} ${row.partyType} ${row.email ?? ""} ${row.mobile ?? ""}`
          .toLowerCase()
          .includes(term);

      const matchesIssue =
        issueFilter === "All" ||
        row.issues.some((issue) => {
          const normalizedIssue = issue.toLowerCase();

          if (issueFilter === "Unverified Email") {
            return normalizedIssue === "email not verified";
          }

          if (issueFilter === "Unverified Mobile") {
            return normalizedIssue === "mobile not verified";
          }

          if (issueFilter === "Not Linked To Service") {
            return normalizedIssue === "not linked to service";
          }

          return normalizedIssue === issueFilter.toLowerCase();
        });

      return matchesSearch && matchesIssue;
    });
  }, [issueFilter, rows, search]);

  const openParty = (row: ContactIssueRow) => {
    history.push(`/party/${row.partyId}`, {
      partyId: row.partyId,
      partyType: row.partyType === "Organisation" ? "Organisation" : "Person",
    });
  };

  const columns: GridColDef<ContactIssueRow>[] = [
    {
      field: "displayName",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "partyType",
      headerName: "Party Type",
      width: 130,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 190,
      valueGetter: (params) => params.row.email || "-",
    },
    {
      field: "emailStatus",
      headerName: "Email Status",
      width: 140,
      valueGetter: (params) => getEmailStatus(params.row),
      renderCell: (params) => {
        const status = getEmailStatus(params.row);
        return <span className={getStatusClass(status)}>{status}</span>;
      },
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: 0.8,
      minWidth: 150,
      valueGetter: (params) => params.row.mobile || "-",
    },
    {
      field: "mobileStatus",
      headerName: "Mobile Status",
      width: 150,
      valueGetter: (params) => getMobileStatus(params.row),
      renderCell: (params) => {
        const status = getMobileStatus(params.row);
        return <span className={getStatusClass(status)}>{status}</span>;
      },
    },
    {
      field: "issues",
      headerName: "Issues",
      flex: 1.2,
      minWidth: 240,
      sortable: false,
      renderCell: (params) => (
        <span className="contacts-report-issues">
          {params.row.issues.map((issue) => (
            <Chip size="small" label={issue} key={issue} />
          ))}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button size="small" onClick={() => openParty(params.row)}>
          View / Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="page-container contacts-report-page">
      <PageHeader
        title="Contacts Report"
        subtitle="Review missing and unverified party contact information."
      />

      <div className="filter-header registration-session-filter contacts-report-filters">
        <div className="form-row registration-session-filter-row contacts-report-filter-row">
          <div className="form-group registration-session-filter-field">
            <input
              className="form-input registration-session-select"
              placeholder="Search by name"
              aria-label="Search by name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="form-group registration-session-filter-field">
            <select
              className="form-input registration-session-select"
              aria-label="Issue type"
              value={issueFilter}
              onChange={(event) =>
                setIssueFilter(event.target.value as IssueFilter)
              }
            >
              {issueFilters.map((filter) => (
                <option value={filter} key={filter}>
                  {filter}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="notification notification--error">{error}</div>}

      <section className="card contacts-report-grid-card">
        <div className="contacts-report-count">
          {filteredRows.length} contact issues
        </div>

        <FrontlineDataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          className="contacts-report-grid"
        />
      </section>
    </div>
  );
}
